/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-empty-function */
import { useCallback, useEffect, useState } from "react";
import { ACCOUNT_SID, BOOKING_API_URL } from "../../constants";
import { HostAuth } from "../../types/ciptex-sdk";
import { NotifiedToken, NotifiedUser } from "../../types";
import { BookingClient } from "../../clients/bookingClient";
import { Configuration, Host, ParticipantVerify, Role } from "@mmc/conferencing-booking-client";
import { useSearchParams } from "react-router-dom";

enum SessionStateKeys {
	UserAuth = "notified_auth[user]",
	HostAuth = "notified_auth[host]",
	NotifiedAuth = "notified_auth[notified]"
}

type HostAuthData = HostAuth;
type SpeakerAuthData = NotifiedToken;
type ParticipantAuthData = ParticipantVerify & { pwd: string };

export enum AuthError {
	UnknownError = "UnknownError",
	BlankResponse = "BlankResponse",
	PasscodeMissing = "PasscodeMissing",
	PasscodeIncorrect = "PasscodeIncorrect",
	PasscodeExpired = "PasscodeExpired",
	ConferenceFinished = "ConferenceFinished"
}

const getErrorType = (message: string): AuthError => {
	switch (message?.toLowerCase()) {
	case "passcode incorrect":
	case "invalid pin":
		return AuthError.PasscodeIncorrect;
	case "passcode expired":
		return AuthError.PasscodeExpired;
	case "this conference has already finished":
		return AuthError.ConferenceFinished;
	default:
		return AuthError.UnknownError;
	}
}

/**
 * Core logic for loading auth data from session storage.
 * Returns auth data if it's up-to-date with query params, or null if not found or invalid.
 */
function loadAuthData<T extends { token: string }>(key: SessionStateKeys, tokenFromQuery: string | null): T | null {
	const tokenFromSession = loadFromSessionStorage<T>(key)?.token;
	// If no token may be found in session, it means no auth data is stored
	if (!tokenFromSession) {
		return null;
	}

	// If we have a token coming in from the query params, and it's not yet stored
	// in the session, it's a new one and we have to re-authenticate
	if (tokenFromQuery && tokenFromQuery !== tokenFromSession) {
		return null;
	}

	return loadFromSessionStorage<T>(key);
}

function loadFromSessionStorage<T>(key: string): T | null {
	const raw = sessionStorage.getItem(key);

	if (!raw) {
		return null;
	}

	try {
		return JSON.parse(raw);
	} catch (e) {
		return null;
	}
}

export const useCiptexAuth = () => {
	const [searchParams] = useSearchParams();
	const tokenFromQueryParams = searchParams.get("t");
	const pwdFromQueryParams = searchParams.get("pwd");

	const [user, setUser] = useState<ParticipantAuthData | null>(loadAuthData<ParticipantAuthData>(SessionStateKeys.UserAuth, tokenFromQueryParams));
	const [host, setHost] = useState<HostAuthData | null>(loadAuthData<HostAuthData>(SessionStateKeys.HostAuth, tokenFromQueryParams));
	const [notified, setNotified] = useState<SpeakerAuthData | null>(loadAuthData<SpeakerAuthData>(SessionStateKeys.NotifiedAuth, tokenFromQueryParams));
	const [isAuthReady, setIsAuthReady] = useState(false);

	const isStaleViewerSession = !!pwdFromQueryParams && user?.pwd !== pwdFromQueryParams;
	const effectiveUser = isStaleViewerSession ? null : user;

	useEffect(() => {
		if (isStaleViewerSession) {
			setUser(null);
		}
	}, [isStaleViewerSession]);

	const authNotified = useCallback(async (notifiedId: string, token: string, params: NotifiedUser) => {
		if(token && notifiedId) {
			const isExternalHost = notifiedId && notifiedId.startsWith("XI");

			setNotified({
				...params,
				token,
				role: isExternalHost ? Role.Host : Role.Speaker,
				notifiedId
			});
		}
	}, []);

	const authHost = useCallback(async (hostId: string, token: string) => {
		if(token && hostId)
		{
			const clientauthed = new BookingClient(new Configuration( {
				apiKey: `Bearer ${token}`,
				basePath: BOOKING_API_URL
			}));
			const hostL: Host = await clientauthed.host.hostHostIdGet({ hostId:hostId });
			setHost({
				...hostL,
				token
			});
		}
		else
		{
			throw new Error("NO HOST ID???");
		}
	}, []);

	const authViewer = useCallback(async (pwd: string) => {
		try {
			const client = new BookingClient(new Configuration( {
				basePath: BOOKING_API_URL
			}));

			const participantVerify: ParticipantVerify = await client.participant.verifyAccountSidPost({
				accountSid: ACCOUNT_SID,
				participantVerifyInput: { pin: pwd }
			});
			if (participantVerify) {
				setUser({ ...participantVerify, pwd });
			} else {
				throw new Error(AuthError.BlankResponse);
			}
		} catch (error: any) {
			const responseJson = await error.response.json();
			throw new Error(getErrorType(responseJson.message || error.body?.message || error.message));
		}
	}, []);

	const signOut = useCallback((): Promise<void> => {
		setUser(null);
		setHost(null);
		setNotified(null);
		return Promise.resolve();
	}, []);

	useEffect(() => {
		sessionStorage.setItem("notified_auth[user]", JSON.stringify(user) || "");
	}, [user]);

	useEffect(() => {
		sessionStorage.setItem("notified_auth[host]", JSON.stringify(host) || "");
	}, [host]);

	useEffect(() => {
		sessionStorage.setItem("notified_auth[notified]", JSON.stringify(notified) || "");
	}, [notified]);

	return { user: effectiveUser, host, notified, isAuthReady, authNotified, authHost, authViewer, signOut };
}
